import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Paper,
} from "@mui/material";
import { Alert } from "@mui/material";
import { CheckCircle, Person, Build } from "@mui/icons-material";
import { useCranes, useCraneSession } from "../../../../hooks";
import { useProfiles } from "../../../../hooks/core/useProfiles";
import {
  Crane,
  BilletYardOperator,
  BilletYardOperatorsApiResponse,
} from "../../../../models/authModels";
import {
  getBilletYardOperators,
  loginOperatorInCrane,
  getCraneOfOperator,
  logoffOperatorOfCrane,
} from "../../../../repository/BilletYardInventoryRepository";
import { OperatorConflictModal } from "./OperatorConflictModal";

interface CraneSelectionModalProps {
  open: boolean;
  onClose: () => void;
  selectedCraneId: number | null;
  selectedOperatorId?: number | null;
  onCraneSelect: (craneId: number | null) => void;
  onOperatorSelect?: (operator: BilletYardOperator | null) => void;
}

export const CraneSelectionModal = ({
  open,
  onClose,
  selectedCraneId,
  selectedOperatorId,
  onCraneSelect,
  onOperatorSelect,
}: CraneSelectionModalProps) => {
  // Use the useCranes hook for crane data
  const { cranes, isLoading: cranesLoading, error: cranesError } = useCranes();

  // Use crane session management
  const { sessionError } = useCraneSession();

  // Check if user is BYI Supervisor - supervisors should not call login API
  const { isSupervisor } = useProfiles();

  // Operator state
  const [operators, setOperators] = useState<BilletYardOperator[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [operatorsError, setOperatorsError] = useState<string | null>(null);
  const [selectedOperator, setSelectedOperator] =
    useState<BilletYardOperator | null>(null);

  // Crane section state
  const [isCraneSectionEnabled, setIsCraneSectionEnabled] = useState(false);
  const [isLoadingOperatorCrane, setIsLoadingOperatorCrane] = useState(false);

  // Login state
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Warning modal state for operator conflict
  const [showOperatorConflictModal, setShowOperatorConflictModal] =
    useState(false);

  // Crane selection without login (login happens on CLOSE with both crane + operator)
  const handleCraneSelect = (crane: Crane) => {
    // Clear any previous login errors when selecting a new crane
    setLoginError(null);

    // Save crane to localStorage for persistence across sessions
    localStorage.setItem("selectedCraneId", crane.CraneID.toString());

    // Just select the crane - login will happen when CLOSE is pressed with both crane + operator
    onCraneSelect(crane.CraneID);
    // Keep modal open to allow operator selection
  };

  // Load operators when modal opens
  useEffect(() => {
    if (open && operators.length === 0) {
      loadOperators();
    }
  }, [open]);

  // Reset crane section state and selections when modal opens (for fresh selection flow)
  useEffect(() => {
    if (open) {
      setIsCraneSectionEnabled(false);
      setIsLoadingOperatorCrane(false);
      // Reset operator selection for fresh start (user must select operator first)
      setSelectedOperator(null);
      // Clear any previous login errors
      setLoginError(null);
    }
  }, [open]);

  // Set initial operator selection based on current operator session
  useEffect(() => {
    if (selectedOperatorId && operators.length > 0) {
      const currentOperator = operators.find(
        (op) => op.OperatorId === selectedOperatorId,
      );
      if (currentOperator) {
        setSelectedOperator(currentOperator);
      }
    }
  }, [selectedOperatorId, operators]);

  const loadOperators = async () => {
    setOperatorsLoading(true);
    setOperatorsError(null);

    try {
      const response =
        (await getBilletYardOperators()) as BilletYardOperatorsApiResponse;

      if (response.ok && response.data?.tables?.[0]?.rows) {
        const operatorsData = response.data.tables[0].rows;

        // Filter out Supervisor (-1) and Torches (-2) operators
        const filteredOperators = operatorsData.filter(
          (operator) =>
            operator.OperatorId !== -1 && operator.OperatorId !== -2,
        );

        setOperators(filteredOperators);
      } else {
        throw new Error(response.message || "Failed to load operators");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error loading operators";
      /* Error handled silently */
      setOperatorsError(errorMessage);
    } finally {
      setOperatorsLoading(false);
    }
  };

  const handleOperatorClick = async (operator: BilletYardOperator) => {
    setSelectedOperator(operator);

    // Clear any previous login errors
    setLoginError(null);

    // Save operator to localStorage for persistence across sessions
    localStorage.setItem("selectedOperatorId", operator.OperatorId.toString());

    if (onOperatorSelect) {
      onOperatorSelect(operator);
    }

    // STEP 1: Reset crane selection first (clear any previous selection)
    onCraneSelect(0); // Set to 0 (no crane selected)
    localStorage.removeItem("selectedCraneId");

    // STEP 2: Call API to get the operator's assigned crane from database
    setIsLoadingOperatorCrane(true);
    try {
      const response = await getCraneOfOperator(operator.OperatorId);

      // Check if operator has an assigned crane
      const assignedCraneId = response?.ok
        ? response.data?.tables?.[0]?.rows?.[0]?.CraneID
        : null;

      // STEP 3: Pre-select crane only if found in database and is a valid crane
      if (assignedCraneId && assignedCraneId !== 0) {
        // Pre-select the assigned crane
        onCraneSelect(assignedCraneId);
        localStorage.setItem("selectedCraneId", assignedCraneId.toString());
      }
      // If no crane assigned (null or 0), all cranes remain unselected (handled by reset above)
    } catch (error) {
      // If API fails, continue without pre-selecting a crane (all cranes unselected)
      console.error("Failed to get operator's crane:", error);
    } finally {
      setIsLoadingOperatorCrane(false);
      // Enable crane section after API response (regardless of success/failure)
      setIsCraneSectionEnabled(true);
    }
  };

  const handleLogin = async (bypassOperatorCheck: boolean = false) => {
    // Clear any previous login errors
    setLoginError(null);

    // Skip login API call for BYI Supervisors - they don't need to login to cranes
    if (isSupervisor) {
      // Supervisors bypass login - just close modal
      setSelectedOperator(null);
      onClose();
      return;
    }

    // Call loginOperatorInCrane API if both crane and operator are selected (for non-supervisors)
    if (selectedCraneId && selectedOperator) {
      try {
        setIsLoggingIn(true);

        const response = await loginOperatorInCrane(
          selectedCraneId,
          selectedOperator.OperatorId,
          true, // checkValidation
          !bypassOperatorCheck, // checkOperatorLogged - false when bypassing
          true, // checkProcParams
          true, // checkExistence
        );

        // Check if login was successful
        const responseId =
          response?.ok && "data" in response
            ? response.data?.tables?.[0]?.rows?.[0]?.ResponseID
            : undefined;

        const errorMessage =
          response?.ok && "data" in response
            ? response.data?.tables?.[0]?.rows?.[0]?.Message
            : response?.message;

        if (response?.ok && responseId === 0) {
          // Login successful (ResponseID = 0 and StatusCode = "A" for Accepted)
          setSelectedOperator(null);
          setLoginError(null);
          onClose();
        } else {
          // Check if this is the "another operator logged in" error
          if (
            errorMessage &&
            errorMessage.includes(
              "There is another operator logged in the crane",
            )
          ) {
            // Show warning modal instead of error alert
            setShowOperatorConflictModal(true);
          } else {
            // Other errors - show as normal error alert
            setLoginError(errorMessage || "Login failed. Please try again.");
          }
          // DO NOT close modal - let user see the error and try again
        }
      } catch (error) {
        // API call failed - show error and keep modal open
        const errorMsg =
          error instanceof Error ? error.message : "Unknown error occurred";
        setLoginError(`Failed to login: ${errorMsg}`);
        // DO NOT close modal
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  // Check if both crane and operator are selected to enable LOGIN button
  const canLogin =
    selectedCraneId !== null &&
    selectedCraneId !== 0 &&
    selectedOperator !== null;
  const buttonText = "LOGIN";

  const isSelectedCrane = (craneId: number) => {
    return selectedCraneId === craneId;
  };

  const isSelectedOperator = (operatorId: number) => {
    return selectedOperator?.OperatorId === operatorId;
  };

  const handleClose = () => {
    // Only allow close if not logging in
    if (!isLoggingIn) {
      // Clear selections when closing without logging in
      setSelectedOperator(null);
      onCraneSelect(null);
      if (onOperatorSelect) {
        onOperatorSelect(null);
      }
      localStorage.removeItem("selectedCraneId");
      localStorage.removeItem("selectedOperatorId");
      onClose();
    }
  };

  const handleConflictCancel = () => {
    // Close the warning modal and clear selections
    setShowOperatorConflictModal(false);
    setSelectedOperator(null);
    onCraneSelect(null);
    if (onOperatorSelect) {
      onOperatorSelect(null);
    }
    localStorage.removeItem("selectedCraneId");
    localStorage.removeItem("selectedOperatorId");
  };

  const handleConflictProceed = async () => {
    // Close the warning modal
    setShowOperatorConflictModal(false);

    // Log off the current operator from the crane
    if (selectedCraneId) {
      try {
        setIsLoggingIn(true);
        await logoffOperatorOfCrane(selectedCraneId);

        // Retry login with operator check bypassed
        await handleLogin(true);
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Unknown error occurred";
        setLoginError(`Failed to logoff previous operator: ${errorMsg}`);
      } finally {
        setIsLoggingIn(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullScreen>
      <DialogTitle
        sx={{
          backgroundColor: "(theme) => theme.palette.primary.main",
          borderBottom:
            "1px solid (theme) => theme.palette.billetYard.borders.medium",
          padding: "12px 24px",
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            color: "white",
            textAlign: "center",
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Select Operator and Crane
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          height: "calc(100vh - 120px)",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Display login error/warning message at the top */}
        {loginError && (
          <Alert
            severity="error"
            onClose={() => setLoginError(null)}
            sx={{ marginBottom: "16px" }}
          >
            {loginError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ height: "100%" }}>
          {/* Left Column - Operators */}
          <Grid item xs={8}>
            <Paper elevation={2} sx={{ height: "100%", padding: "16px" }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Person
                  sx={{
                    marginRight: "8px",
                    color: "(theme) => theme.palette.primary.main",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "(theme) => theme.palette.primary.main",
                  }}
                >
                  Select Operator
                </Typography>
              </Box>

              {operatorsLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    sx={{ marginLeft: 16, alignSelf: "center" }}
                  >
                    Loading operators...
                  </Typography>
                </Box>
              )}

              {operatorsError && (
                <Alert
                  severity="error"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={loadOperators}
                    >
                      Retry
                    </Button>
                  }
                >
                  {operatorsError}
                </Alert>
              )}

              {!operatorsLoading && !operatorsError && operators.length > 0 && (
                <Box sx={{ maxHeight: "calc(100% - 80px)", overflow: "auto" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr 1fr 1fr",
                      gap: "8px",
                      padding: "4px",
                    }}
                  >
                    {operators
                      .filter(
                        (operator) =>
                          operator.OperatorId !== -1 &&
                          operator.OperatorId !== -2,
                      )
                      .map((operator) => {
                        const isSelected = isSelectedOperator(
                          operator.OperatorId,
                        );
                        const isCurrent =
                          selectedOperatorId === operator.OperatorId;

                        return (
                          <Box
                            key={operator.OperatorId}
                            onClick={() => handleOperatorClick(operator)}
                            sx={{
                              minHeight: "80px",
                              border: (theme) =>
                                `2px solid ${theme.palette.billetYard.borders.medium}`,
                              borderRadius: (theme) => theme.spacing(1),
                              backgroundColor: (theme) =>
                                isSelected
                                  ? theme.palette.billetYard.loginSelected
                                      .background
                                  : "white",
                              borderColor: (theme) =>
                                isSelected
                                  ? theme.palette.billetYard.loginSelected
                                      .border
                                  : theme.palette.billetYard.borders.medium,
                              boxShadow: (theme) =>
                                isSelected
                                  ? `0 2px 8px ${theme.palette.billetYard.loginSelected.shadow}`
                                  : "none",
                              cursor: "pointer",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: (theme) => theme.spacing(2),
                              transition: "all 0.2s ease",
                              position: "relative",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  isSelected
                                    ? theme.palette.billetYard.loginSelected
                                        .hover
                                    : theme.palette.billetYard.backgrounds
                                        .light,
                                borderColor: (theme) =>
                                  isSelected
                                    ? theme.palette.billetYard.loginSelected
                                        .border
                                    : theme.palette.grey[400],
                                transform: "translateY(-2px)",
                                boxShadow: (theme) =>
                                  isSelected
                                    ? `0 2px 8px ${theme.palette.billetYard.loginSelected.shadow}`
                                    : "0 4px 8px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            <Person
                              sx={{
                                fontSize: "18px",
                                color: "(theme) => theme.palette.primary.main",
                                marginBottom: "4px",
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: "bold",
                                fontSize: "16px",
                                textAlign: "center",
                                color: "(theme) => theme.palette.text.primary",
                              }}
                            >
                              {operator.OperatorName}
                            </Typography>

                            {isSelected && (
                              <CheckCircle
                                color="primary"
                                sx={{
                                  position: "absolute",
                                  bottom: "8px",
                                  left: "8px",
                                  fontSize: "20px",
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              )}

              {!operatorsLoading &&
                !operatorsError &&
                operators.length === 0 && (
                  <Alert severity="info">
                    No operators found. Please contact your system
                    administrator.
                  </Alert>
                )}
            </Paper>
          </Grid>

          {/* Right Column - Cranes */}
          <Grid item xs={4}>
            <Paper
              elevation={2}
              sx={{
                height: "100%",
                padding: "16px",
                opacity: isCraneSectionEnabled ? 1 : 0.5,
                pointerEvents: isCraneSectionEnabled ? "auto" : "none",
              }}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Build
                  sx={{
                    marginRight: "8px",
                    color: "(theme) => theme.palette.primary.main",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "(theme) => theme.palette.primary.main",
                  }}
                >
                  Select Crane
                </Typography>
              </Box>

              {!isCraneSectionEnabled && (
                <Alert
                  severity="info"
                  sx={{
                    margin: (theme) => theme.spacing(2, 0),
                  }}
                >
                  Please select an operator first
                </Alert>
              )}

              {isLoadingOperatorCrane && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    sx={{ marginLeft: 16, alignSelf: "center" }}
                  >
                    Loading operator's crane...
                  </Typography>
                </Box>
              )}

              {isCraneSectionEnabled && cranesLoading && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 300,
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    variant="body2"
                    sx={{ marginLeft: 16, alignSelf: "center" }}
                  >
                    Loading cranes...
                  </Typography>
                </Box>
              )}

              {isCraneSectionEnabled && cranesError && (
                <Alert
                  severity="error"
                  sx={{
                    margin: (theme) => theme.spacing(2, 0),
                  }}
                >
                  {cranesError}
                </Alert>
              )}

              {isCraneSectionEnabled && sessionError && (
                <Alert
                  severity="error"
                  sx={{
                    margin: (theme) => theme.spacing(2, 0),
                  }}
                >
                  Crane Login Error: {sessionError}
                </Alert>
              )}

              {isCraneSectionEnabled &&
                !isLoadingOperatorCrane &&
                !cranesLoading &&
                !cranesError &&
                cranes.length > 0 && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "8px",
                      padding: "4px",
                    }}
                  >
                    {cranes.map((crane) => (
                      <Box
                        key={crane.CraneID}
                        onClick={() => handleCraneSelect(crane)}
                        sx={{
                          minHeight: "80px",
                          border: (theme) =>
                            `2px solid ${theme.palette.billetYard.borders.medium}`,
                          borderRadius: (theme) => theme.spacing(1),
                          backgroundColor: (theme) =>
                            isSelectedCrane(crane.CraneID)
                              ? theme.palette.billetYard.loginSelected
                                  .background
                              : "white",
                          borderColor: (theme) =>
                            isSelectedCrane(crane.CraneID)
                              ? theme.palette.billetYard.loginSelected.border
                              : theme.palette.billetYard.borders.medium,
                          boxShadow: (theme) =>
                            isSelectedCrane(crane.CraneID)
                              ? `0 2px 8px ${theme.palette.billetYard.loginSelected.shadow}`
                              : "none",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: (theme) => theme.spacing(2),
                          transition: "all 0.2s ease",
                          position: "relative",
                          "&:hover": {
                            backgroundColor: (theme) =>
                              isSelectedCrane(crane.CraneID)
                                ? theme.palette.billetYard.loginSelected.hover
                                : theme.palette.billetYard.backgrounds.light,
                            borderColor: (theme) =>
                              isSelectedCrane(crane.CraneID)
                                ? theme.palette.billetYard.loginSelected.border
                                : theme.palette.grey[400],
                            transform: "translateY(-2px)",
                            boxShadow: (theme) =>
                              isSelectedCrane(crane.CraneID)
                                ? `0 2px 8px ${theme.palette.billetYard.loginSelected.shadow}`
                                : "0 4px 8px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            fontSize: "16px",
                            textAlign: "center",
                            color: "(theme) => theme.palette.text.primary",
                          }}
                        >
                          {crane.CraneName}
                        </Typography>

                        {crane.CraneID === 0 && (
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "(theme) => theme.palette.text.secondary",
                              marginTop: (theme) => theme.spacing(0.5),
                              textAlign: "center",
                            }}
                          >
                            Select a crane to begin
                          </Typography>
                        )}

                        {isSelectedCrane(crane.CraneID) && (
                          <CheckCircle
                            color="primary"
                            sx={{
                              position: "absolute",
                              bottom: "8px",
                              left: "8px",
                              fontSize: "20px",
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

              {isCraneSectionEnabled &&
                !isLoadingOperatorCrane &&
                !cranesLoading &&
                !cranesError &&
                cranes.length === 0 && (
                  <Alert severity="info">
                    No cranes available. Please contact your administrator.
                  </Alert>
                )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "24px",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          gap: (theme) => theme.spacing(3),
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          disabled={isLoggingIn}
          sx={{
            minWidth: "150px",
            minHeight: "56px",
            fontSize: "18px",
            fontWeight: "bold",
            padding: "16px 32px",
            borderRadius: "8px",
            backgroundColor:
              "(theme) => theme.palette.billetYard.backgrounds.light",
            color: "(theme) => theme.palette.text.primary",
            border:
              "1px solid (theme) => theme.palette.billetYard.borders.dark",
            boxShadow: "0 4px 14px 0 rgba(158, 158, 158, 0.25)",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor:
                "(theme) => theme.palette.billetYard.borders.medium",
            },
          }}
        >
          CLOSE
        </Button>
        <Button
          onClick={canLogin ? () => handleLogin() : undefined}
          variant="contained"
          color={canLogin ? "primary" : "inherit"}
          size="large"
          disabled={!canLogin || isLoggingIn}
          startIcon={
            isLoggingIn ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
          sx={{
            minWidth: "150px",
            minHeight: "56px",
            fontSize: "18px",
            fontWeight: "bold",
            padding: "16px 32px",
            borderRadius: "8px",
          }}
        >
          {isLoggingIn ? "LOGGING IN..." : buttonText}
        </Button>
      </DialogActions>

      {/* Operator Conflict Warning Modal */}
      <OperatorConflictModal
        open={showOperatorConflictModal}
        onCancel={handleConflictCancel}
        onProceed={handleConflictProceed}
      />
    </Dialog>
  );
};
